'use client'
import Image from 'next/image'
import { GoogleMap, InfoWindow, Marker, useJsApiLoader } from '@react-google-maps/api'

import { FC, useState } from 'react'

import { AnimationMoveIconComponent } from '@/app/shared/components/animation/move-icon'
import { AnimationShowBlockComponent } from '@/app/shared/components/animation/show-block'
import { PlaceComponent } from '@/app/shared/components/position/elements/place'
import { IconArrow, IconPlace } from '@/app/shared/icons'
import { IId } from '@/app/shared/interfaces/common.interface'
import { IContent, IImg, ISubTitle, ITitle } from '@/app/shared/interfaces/content.interface'
import useLayout from '@/app/shared/store/layout.store'
import { AccordionComponent } from '@/app/shared/ui/accordion'
import { ContainerComponent } from '@/app/shared/ui/container'
import { TitleComponent } from '@/app/shared/ui/title'

// interface
interface IPosition extends IId {
  data: IPositionQuery
}

export interface IPositionQuery {
  title: ITitle
  subTitle: ISubTitle
  sticker: IImg
  arrayList: {
    title: string | null
    items: IContent[]
  }
}

const center = {
  lat: 50.5,
  lng: 30.5,
}

// component
const PositionComponent: FC<Readonly<IPosition>> = ({ data, id }) => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS ?? '',
  })

  const [activeMarker, setActiveMarker] = useState<number | null>(null)

  const handleActiveMarker = (marker: number) => {
    if (marker === activeMarker) {
      return
    }
    setActiveMarker(marker)
  }
  const markers =
    useLayout((state) => state.affiliates)
      ?.filter((el) => el?.lat)
      ?.map((el) => ({
        id: el?.id,
        name: el?.address,
        position: { lat: el?.lat ?? 0, lng: el?.lng ?? 0 },
      })) ?? []

  const arrayListItem = useLayout((state) => state.affiliates)
    ?.filter((el) => el?.lat)
    ?.map((el) => ({
      title: el?.metro,
      content: <PlaceComponent data={el} />,
    }))

  // return
  return (
    <ContainerComponent id={id} className={'bg-success pb-16 pt-20 text-content1-600'}>
      <div className={'grid gap-[50px]'}>
        <div className={'relative grid gap-5 desktop:w-[709px]'}>
          <TitleComponent
            typeTag={data.title.size}
            classSize={'text-[28px] extraMobile:text-[34px]'}
            className={'w-[80%] desktop:w-full'}
            animate
          >
            {data.title.title}
          </TitleComponent>
          <AnimationShowBlockComponent
            typeTag={'span'}
            className={'text-16px text-black desktop:text-[24x]'}
            initial={{
              opacity: 0,
              y: -20,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
          >
            {data.subTitle.subTitle}
          </AnimationShowBlockComponent>
          <AnimationMoveIconComponent
            className={
              'absolute right-0 top-[-20px] h-[75px] w-[100px] rotate-[25deg] desktop:-bottom-[30px] desktop:-right-[198px] large:h-[117px] large:w-[88px]'
            }
            rotate={25}
            valueMoving={55}
            initial={{
              opacity: 0,
            }}
            whileInView={{
              opacity: 1,
            }}
            transition={{
              delay: 1,
            }}
          >
            <Image
              src={data.sticker.image}
              alt={'marker'}
              className={'object-contain'}
              fill
              sizes={'(max-width: 1920px):100px, 88px'}
            />
          </AnimationMoveIconComponent>
        </div>
        <AnimationShowBlockComponent
          className={
            'grid grid-cols-1 gap-5 laptop:grid-cols-[1fr_347px] desktop:grid-cols-[1fr_373px] large:grid-cols-[1fr_480px]'
          }
          initial={{
            opacity: 0,
            y: -20,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
        >
          {isLoaded ? (
            <GoogleMap center={center} zoom={10}>
              {markers?.map(({ id, name, position }) => (
                <Marker
                  key={id}
                  position={position}
                  onClick={() => id && handleActiveMarker(Number(id))}
                >
                  {activeMarker === Number(id) ? (
                    <InfoWindow position={position} onCloseClick={() => setActiveMarker(null)}>
                      <p className={'text-black'}>{name}</p>
                    </InfoWindow>
                  ) : null}
                </Marker>
              ))}
            </GoogleMap>
          ) : (
            <p>loading...</p>
          )}
          <div className={'max-laptop:-ml-2 max-laptop:w-[100%+1rem]'}>
            <AccordionComponent
              data={arrayListItem}
              startContent={<IconPlace className={'h-[30px] w-[30px]'} />}
              indicator={({ isOpen }) =>
                isOpen ? (
                  <IconArrow className={'h-[28px] w-[21px] rotate-[270deg]'} />
                ) : (
                  <IconArrow className={'h-[28px] w-[21px]'} />
                )
              }
            />
          </div>
        </AnimationShowBlockComponent>
      </div>
    </ContainerComponent>
  )
}

export default PositionComponent
